import React, { useEffect, useState } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import "../Style/Dashboard.css";
import "../Style/Emergency.css";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL } from "../Apis/apiConfig";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AlertCircle, MapPin, Phone, Map, Building, Save, List, Siren, Edit3
} from "lucide-react";

const EditEmergency = () => {
  const navigate = useNavigate();
  const { emergencyId } = useParams();
  const societyName = localStorage.getItem("society") || "";
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({emergencyId: "", dials: "",
    city: "",
    address: "",
    phoneNumber: "",
    state: "",
  });

  const [loading, setLoading] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const pick = (obj, keys, fallback = "") => {
    if (!obj) return fallback;
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return fallback;
  };

  useEffect(() => {
    const fetchEmergency = async () => {
      try {
        if (!emergencyId) {
          console.warn("No emergencyId in URL params");
          return;
        }
        const idNum = Number(emergencyId);
        if (isNaN(idNum)) {
          console.error("Invalid emergencyId (not a number):", emergencyId);
          return;
        }

        if (!societyName) {
          console.error("Missing society name in localStorage");
          toast.error("Missing society name. Please login/select society.", {
            position: "bottom-right",
            autoClose: 3000,
          });
          return;
        }

        const token = localStorage.getItem("token");
        if (!token) {
          console.error("Missing token in localStorage");
          toast.error("You are not authorized (missing token).", {
            position: "bottom-right",
            autoClose: 3000,
          });
          return;
        }

        setLoading(true);

        const response = await axios.get(
          `${BASE_URL}/emergency/get-by-emergencyId`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              emergencyId: idNum,
              societyName: societyName,
            },
            validateStatus: (status) => true,
          }
        );

        console.log("GET /get-by-emergencyId => status:", response.status, "data:", response.data);

        if (response.status === 401 || response.status === 403) {
          toast.error("Unauthorized. Please login again.", {
            position: "bottom-right",
            autoClose: 3000,
          });
          return;
        }

        if (response.status >= 400) {
          console.error("Server returned error:", response.status, response.data);
          toast.error("Failed to fetch emergency. See console for details.", {
            position: "bottom-right",
            autoClose: 3000,
          });
          return;
        }

        let emergencyData = response.data;
        if (Array.isArray(emergencyData)) {
          emergencyData = emergencyData.length > 0 ? emergencyData[0] : null;
        } else if (emergencyData && typeof emergencyData === "object" && emergencyData.hasOwnProperty("data")) {
          emergencyData = emergencyData.data;
          if (Array.isArray(emergencyData)) emergencyData = emergencyData.length > 0 ? emergencyData[0] : null;
        }

        if (!emergencyData) {
          console.warn("No emergency record found for id:", idNum);
          toast.error("No emergency found for given ID.", {
            position: "bottom-right",
            autoClose: 3000,
          });
          return;
        }
        setFormData({
          emergencyId: idNum,
          dials: String(pick(emergencyData, ["emergency", "dials", "dial", "name"], "")),
          city: String(pick(emergencyData, ["city", "town", "location"], "")),
          address: String(pick(emergencyData, ["address", "addr", "location_detail"], "")),
          phoneNumber: String(pick(emergencyData, ["phoneNumber", "phone", "mobile", "contact"], "")),
          state: String(pick(emergencyData, ["state", "region", "province"], "")),
        });

      } catch (error) {
        console.error("Error fetching emergency:", error);
        toast.error("Failed to load emergency. Check console for details.", {
          position: "bottom-right",
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    if (emergencyId) fetchEmergency();
  }, [emergencyId, societyName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("Missing token. Please login.", {
          position: "bottom-right",
          autoClose: 3000,
        });
        return;
      }
      const formParams = new URLSearchParams();
      formParams.append("emergencyId", String(formData.emergencyId));
      formParams.append("dials", formData.dials || "");
      formParams.append("city", formData.city || "");
      formParams.append("address", formData.address || "");
      formParams.append("phoneNumber", formData.phoneNumber || "");
      formParams.append("state", formData.state || "");
      formParams.append("societyName", societyName || "");

      console.log("Submitting update with params:", Object.fromEntries(formParams.entries()));

      const res = await axios.post(
        `${BASE_URL}/emergency/update-emergency`,
        formParams,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          validateStatus: (status) => true,
        }
      );

      console.log("POST /update-emergency =>", res.status, res.data);
      if (res.status >= 400) {
        toast.error("Update failed. See console for details.", {
          position: "bottom-right",
          autoClose: 3000,
        });
        return;
      }

      toast.success("Emergency updated successfully ", {
        position: "bottom-right",
        autoClose: 3000,
      });
      navigate("/emergency-list");
    } catch (error) {
      console.error("Error updating emergency:", error);
      toast.error("Failed to update emergency ", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container">
      <Header toggleSidebar={toggleSidebar} />
      <ToastContainer />

      <div className="main-layout">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <main className="content-area">
          <SubHeader />

          <div className="emergency-container">
            <div className="emergency-card">
              <div className="emergency-header">
                <div className="emergency-title-group">
                  <Edit3 size={28} />
                  <h2>Edit Emergency Contact</h2>
                </div>
                <button
                  type="button"
                  className="show-data-btn"
                  onClick={() => navigate("/emergency-list")}
                >
                  <List size={18} />
                  <span>View All</span>
                </button>
              </div>

              <form className="emergency-form-body" onSubmit={handleSubmit}>
                <div className="form-grid-emergency">
                  <div className="form-field-emergency">
                    <label className="form-label-emergency">
                      <AlertCircle size={16} />
                      <span>Emergency Type</span>
                      <span className="required">*</span>
                    </label>
                    <select
                      name="dials"
                      value={formData.dials}
                      onChange={handleChange}
                      required
                      className="form-select-emergency"
                    >
                      <option value="">-- Select Emergency Type --</option>
                      <option value="AMBULANCE">AMBULANCE</option>
                      <option value="FIRE BRIGADE">FIRE BRIGADE</option>
                      <option value="HOSPITAL">HOSPITAL</option>
                      <option value="POLICE">POLICE</option>
                      <option value="Women Helpline">Women Helpline</option>
                      <option value="Child Helpline">Child Helpline</option>
                      <option value="Forest">Forest</option>
                      <option value="Gas Leakage (LPG)">Gas Leakage (LPG)</option>
                    </select>
                  </div>

                  <div className="form-field-emergency">
                    <label className="form-label-emergency">
                      <Building size={16} />
                      <span>City</span>
                      <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      placeholder="Enter city name"
                      className="form-input-emergency"
                    />
                  </div>

                  <div className="form-field-emergency">
                    <label className="form-label-emergency">
                      <MapPin size={16} />
                      <span>Address</span>
                      <span className="required">*</span>
                    </label>
                    <input type="text" name="address" value={formData.address} 
                      onChange={handleChange}
                      required
                      placeholder="Enter full address"
                      className="form-input-emergency"
                    />
                  </div>

                  <div className="form-field-emergency">
                    <label className="form-label-emergency">
                      <Phone size={16} />
                      <span>Phone Number</span>
                      <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      required
                      placeholder="Enter phone number"
                      className="form-input-emergency"
                    />
                  </div>

                  <div className="form-field-emergency full-width-emergency">
                    <label className="form-label-emergency">
                      <Map size={16} />
                      <span>State</span>
                      <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      placeholder="Enter state name"
                      className="form-input-emergency"
                    />
                  </div>
                </div>

                <div className="form-actions-emergency">
                  <button type="submit" className="submit-btn-emergency" disabled={loading}>
                    <Save size={18} />
                    <span>{loading ? "Updating..." : "Update Emergency Contact"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default EditEmergency;
